import { MouseEvent } from 'react';
import clsx from 'clsx';

import CheckedIcon from './icons/checked';
import IndeterminateIcon from './icons/indeterminate';
import { ifTrue } from 'src/tools/logic.tools';
import { useId } from 'src/hooks';

import { TextTooltip } from 'src/components/TextTooltip';

import classes from './Checkbox.module.scss';

type Place = 'top' | 'right' | 'bottom' | 'left';

interface Props {
  className?: string;
  title?: string;
  isChecked?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  value?: string;
  label?: string | JSX.Element;
  onClick?: (e: MouseEvent) => void;
  isSmall?: boolean;
  isSmallCheckbox?: boolean;
  tooltip?: string;
  tooltipPosition?: string;
  'data-testid'?: string; // Add this line to define the type for 'data-testid'
}

// new-ui
export function Checkbox({
  className,
  title,
  isChecked,
  isDisabled,
  isIndeterminate: isIndeterminateProp,
  value,
  label,
  onClick,
  isSmall,
  isSmallCheckbox = isSmall,
  tooltip,
  tooltipPosition,
  ...props
}: Props) {
  const isIndeterminate = !isChecked && isIndeterminateProp;

  const tooltipId = useId();

  function handleOnClick(e: MouseEvent) {
    if (!isDisabled) onClick?.(e);
  }

  function renderLabel() {
    return <label>{label}</label>;
  }

  function renderTooltip() {
    return <TextTooltip id={tooltipId} place={tooltipPosition as Place} />;
  }

  return (
    <div
      data-testid={props['data-testid'] ?? 'checkbox-container'}
      title={title}
      className={clsx(classes.checkbox, 'row nowrap a-center items-p4', className, {
        [classes.checked]: isChecked,
        [classes.disabled]: isDisabled,
        [classes.indeterminate]: isIndeterminate,
        [classes.small]: isSmall,
        [classes['small-checkbox']]: isSmallCheckbox,
      })}
      onClick={handleOnClick}
      data-tip={tooltip}
      data-for={tooltipId}
    >
      <div className={clsx('row', classes['input-wrapper'])}>
        <input readOnly data-testid='input' type='checkbox' value={value} checked={isChecked} />
        <CheckedIcon hidden={!isChecked} />
        <IndeterminateIcon hidden={!isIndeterminate} />
      </div>
      {ifTrue(label, renderLabel)}
      {ifTrue(tooltip, renderTooltip)}
    </div>
  );
}
